import { usePostHog } from 'posthog-js/react';
import User from '@/models/User';
import { getPseudonymousId } from '@/helpers/pseudonymousId';
import { isPostHogStarted } from '@/helpers/posthogClient';

/**
 * Custom hook for PostHog user identification
 * Provides functions to identify and reset user in PostHog
 *
 * HAT-1081: לפני הסכמת המשתמש PostHog אינו מאותחל כלל, ולכן כל הפעולות כאן
 * חסומות מאחורי isPostHogStarted. ConsentProvider מזהה מחדש ברגע ההסכמה.
 */
export const usePostHogIdentification = () => {
  const posthog = usePostHog();

  // HAT-1081: המזהה פסאודונימי וה-props מצומצמים ל-non-PII בלבד - בלי מייל,
  // שם או טלפון. אסור להחזיר לכאן spread של אובייקט המשתמש.
  const identifyUser = async (user: User) => {
    if (!posthog || !isPostHogStarted() || !user.email) return;

    const pseudonymousId = await getPseudonymousId(user.email);
    if (!pseudonymousId) return;

    // הסיווג וספירת הקרנות הגיעו מ-master ונשמרים: הם מאפשרים פילוח בלי לזהות
    // אף אחד. מה שירד הוא ה-spread של אובייקט המשתמש והמייל כ-distinct_id.
    const role = user.roles?.includes('Admin')
      ? 'admin'
      : (user.fundIds?.length ?? 0) > 0
        ? 'fund_manager'
        : 'user';

    posthog.identify(pseudonymousId, {
      host: window.location.host,
      roles: user.roles,
      role,
      fund_count: user.fundIds?.length ?? 0,
    });
  };

  const resetUser = () => {
    if (!isPostHogStarted()) return;

    posthog?.reset();
    // reset() wipes super properties; re-register site so post-logout events stay attributable
    posthog?.register({ site: window.location.host });
  };

  const captureEvent = (eventName: string, properties?: Record<string, unknown>) => {
    if (!isPostHogStarted()) return;

    posthog?.capture(eventName, properties);
  };

  return {
    identifyUser,
    resetUser,
    captureEvent,
    posthog,
  };
};
